import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ClientService } from './client.service';

export interface StudyGoalModel {
  id: number;
  userId: number;
  workspaceId: number;
  dailyCardsTarget: number;
  dailyMinutesTarget: number;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface StudyGoalUpsertPayload {
  dailyCardsTarget: number;
  dailyMinutesTarget: number;
  isActive: boolean;
}

export interface DailyStudyGoalProgressModel {
  date: string;
  dailyCardsTarget: number;
  reviewedCards: number;
  cardsProgressPercent: number;
  dailyMinutesTarget: number;
  activeMinutes: number;
  minutesProgressPercent: number;
  completed: boolean;
  isActive: boolean;
}

type ItemEnvelope<T> =
  | T
  | {
      data?: T;
      item?: T;
      value?: T;
    };

@Injectable({ providedIn: 'root' })
export class StudyGoalService {
  private readonly client = inject(ClientService);

  getCurrent(): Observable<StudyGoalModel> {
    return this.client
      .get<ItemEnvelope<Record<string, unknown>>>('study-goals/current')
      .pipe(map((response) => this.normalizeGoal(this.unwrapItem(response))));
  }

  createCurrent(payload: StudyGoalUpsertPayload): Observable<StudyGoalModel> {
    return this.client
      .post<
        ItemEnvelope<Record<string, unknown>>,
        StudyGoalUpsertPayload
      >('study-goals/current', payload)
      .pipe(map((response) => this.normalizeGoal(this.unwrapItem(response))));
  }

  updateCurrent(payload: StudyGoalUpsertPayload): Observable<StudyGoalModel> {
    return this.client
      .put<
        ItemEnvelope<Record<string, unknown>>,
        StudyGoalUpsertPayload
      >('study-goals/current', payload)
      .pipe(map((response) => this.normalizeGoal(this.unwrapItem(response))));
  }

  deleteCurrent(): Observable<boolean> {
    return this.client.delete<boolean>('study-goals/current');
  }

  getDailyProgress(
    referenceDate?: string,
  ): Observable<DailyStudyGoalProgressModel> {
    const query = referenceDate
      ? `?referenceDate=${encodeURIComponent(referenceDate)}`
      : '';

    return this.client
      .get<
        ItemEnvelope<Record<string, unknown>>
      >(`analytics/study/daily-goal${query}`)
      .pipe(
        map((response) =>
          this.normalizeDailyProgress(this.unwrapItem(response)),
        ),
      );
  }

  private unwrapItem<T>(response: ItemEnvelope<T>): T {
    const wrapped = response as Record<string, unknown>;
    return (
      (wrapped['data'] as T | undefined) ??
      (wrapped['item'] as T | undefined) ??
      (wrapped['value'] as T | undefined) ??
      (response as T)
    );
  }

  private normalizeGoal(record: Record<string, unknown>): StudyGoalModel {
    return {
      id: this.toNumber(record['id'] ?? record['Id']),
      userId: this.toNumber(record['userId'] ?? record['UserId']),
      workspaceId: this.toNumber(
        record['workspaceId'] ?? record['WorkspaceId'],
      ),
      dailyCardsTarget: this.toNumber(
        record['dailyCardsTarget'] ?? record['DailyCardsTarget'],
      ),
      dailyMinutesTarget: this.toNumber(
        record['dailyMinutesTarget'] ?? record['DailyMinutesTarget'],
      ),
      isActive: this.toBoolean(record['isActive'] ?? record['IsActive']),
      createdAt: this.toNullableString(
        record['createdAt'] ?? record['CreatedAt'],
      ),
      updatedAt: this.toNullableString(
        record['updatedAt'] ?? record['UpdatedAt'],
      ),
    };
  }

  private normalizeDailyProgress(
    record: Record<string, unknown>,
  ): DailyStudyGoalProgressModel {
    return {
      date: String(record['date'] ?? record['Date'] ?? ''),
      dailyCardsTarget: this.toNumber(
        record['dailyCardsTarget'] ?? record['DailyCardsTarget'],
      ),
      reviewedCards: this.toNumber(
        record['reviewedCards'] ?? record['ReviewedCards'],
      ),
      cardsProgressPercent: this.toNumber(
        record['cardsProgressPercent'] ?? record['CardsProgressPercent'],
      ),
      dailyMinutesTarget: this.toNumber(
        record['dailyMinutesTarget'] ?? record['DailyMinutesTarget'],
      ),
      activeMinutes: this.toNumber(
        record['activeMinutes'] ?? record['ActiveMinutes'],
      ),
      minutesProgressPercent: this.toNumber(
        record['minutesProgressPercent'] ?? record['MinutesProgressPercent'],
      ),
      completed: this.toBoolean(record['completed'] ?? record['Completed']),
      isActive: this.toBoolean(record['isActive'] ?? record['IsActive']),
    };
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    return String(value).toLowerCase() === 'true';
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
  }
}
